import {
  Box,
  Button,
  EmptyCell,
  type IColumn,
  Icon,
  NavLinkVue,
  Status,
  Tooltip,
  TooltipLink,
  Typography,
} from 'shared/ui'
import {
  LINK_AIRCALL_INTEGRATION,
  LINK_AIRCALL_NOT_READY_TO_BE_ASSIGNED,
} from 'shared/constants/links'
import { uiStore } from 'shared/store/uiStore'
import { type AircallNumberRow } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationAircallSettings/model/AircallNumberRow/AircallNumberRow'
import { type AircallNumbersListStore } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationAircallSettings/store/AircallNumbersListStore'
import { LearnMoreLink } from 'pages/settings/ui/LearnMoreLink/LearnMoreLink'
import styles from './styles.module.scss'

export const createAircallColumns = (store: AircallNumbersListStore) => {
  const numberCell: IColumn<AircallNumberRow> = {
    field: 'formatted_number',
    name: 'Number',
    isSortable: true,
    width: '158px',
    sortKey: 'formatted_number',
    renderRowCell: (row) => (
      <div className={styles.numberContent}>
        <div className={styles.numberRow}>
          {row.formatted_number}
          <Icon icon={'aircall'} />
        </div>
      </div>
    ),
  }

  const statusCell: IColumn<AircallNumberRow> = {
    field: 'active',
    name: 'Status',
    renderRowCell: (row) => {
      if (row.assigned) {
        return (
          <div>
            <Status title='Active' intent='positive' emphasis={'high'} icon='check2' />
          </div>
        )
      }
      if (!row.assigned && !row.active) {
        return (
          <div className={styles.actionRequiredStatus}>
            <Icon icon='alertCircle' color='var(--yellow-60)' />
            Action Required
            <Tooltip
              label='Number is not ready to be assigned'
              type='description'
              desc={
                <LearnMoreLink href={LINK_AIRCALL_NOT_READY_TO_BE_ASSIGNED}>
                  {'Learn more'}
                </LearnMoreLink>
              }
              placement='top'
            >
              <Icon icon='question' color='var(--black-70)' />
            </Tooltip>
          </div>
        )
      }
      return (
        <div>
          <Status title='Unassigned' intent='neutral' emphasis={'high'} icon='minusCircle' />
        </div>
      )
    },
  }

  const actionCell: IColumn<AircallNumberRow> = {
    field: 'assigned',
    name: ' ',
    renderRowCell: (row) => (
      <div className={styles.actionCell}>
        {!row.assigned && !row.active && (
          <Button
            text='Contact Aircall'
            tag='a'
            contained='tertiary'
            href={`${LINK_AIRCALL_INTEGRATION}/${row.id}`}
            target='_blank'
            rel='noreferrer'
            size='small'
            className={styles.contactAircallButton}
          />
        )}
        {!row.assigned && (
          <Button
            text='Assign number'
            size='small'
            onClick={() => {
              store.assignNumbers([row.id])
            }}
            disabled={store.isNumbersGracePeriod}
            tooltipProps={
              store.isNumbersGracePeriod
                ? {
                    type: 'description',
                    label: (
                      <Box display='flex' flexDirection='column' gap={0.5}>
                        <Typography
                          variant={'body-md-regular'}
                          color={'var(--content-primary-primary-inverted)'}
                          ariaLabel={'Grace Period'}
                        >
                          Take care of unpaid licenses
                          <br /> before adding new numbers.
                        </Typography>
                      </Box>
                    ),
                    width: 233,
                    placement: 'top',
                    desc: (
                      <TooltipLink
                        text='Manage numbers'
                        onClick={() => {
                          uiStore.changeRoute({
                            path: '/settings/organization/numbers',
                          })
                        }}
                      />
                    ),
                  }
                : undefined
            }
          />
        )}
      </div>
    ),
  }

  const inboxCell: IColumn<AircallNumberRow> = {
    field: 'assigned',
    name: ' ',
    renderRowCell: (row) => {
      const { numberable } = row

      if (!numberable) return <EmptyCell />

      return (
        <NavLinkVue
          to={`/settings/organization/inboxes/all-inboxes/${numberable?.id}`}
          className={styles.inboxCell}
          withoutStyles
        >
          <Icon icon={'inboxOpen'} tertiary />
          <Typography
            ariaLabel='link'
            variant='body-md-regular'
            tooltipProps={{
              label: numberable?.name,
              placement: 'top',
            }}
            ellipsis
          >
            {numberable?.name}
          </Typography>
        </NavLinkVue>
      )
    },
  }

  return { numberCell, statusCell, actionCell, inboxCell }
}
